// src/encounter/care-plan-tab/care-plan-view.tsx
// The encounter's "Care plan" tab (`?x=care-plan-tab`, it takes the header's Timeline slot): Samer's demo "protocol doc"
// (scrips-demo-samer.vercel.app ?view=protocol-doc) drawn from the record. Presentational: the plans come in as
// CarePlanView (contract C5 + C4 step, lib/care-plan-view.ts); nothing is made up.
//  - Trajectory: each goal's measure, target (and its source), latest result and status, as the C5 goal carries them.
//  - Plan board: the plan's header (title, status, current step, period), its goals by track, and the six lanes x 12 weeks
//    from the plan's ACTIVITIES (B1, care-model's contract 4 Nov: lane, owner, weeks, status). Status labels: AI draft (a
//    label only, Andrew 4 Nov), Draft, Accepted · unsigned, Signed. A lane with none says "No plan items yet"; a server
//    that records no activities says so.
import { useState } from 'react';
import { Button, EmptyState, Segmented, StatusChip } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { V2_BODY, V2_BODY_EMPHASIS, V2_CARD_SURFACE, V2_CARD_TITLE, V2_META } from '@/shared/ui/design-v2';
import { CARE_PLAN_LANE_IDS, type CarePlanActivityView, type CarePlanView, type GoalView } from '../lib/care-plan-view';
import type { ActivityAction } from './use-activity-action';
import { ACTIVITY_STATUS_LABEL, CARE_PLAN_LANE_LABEL, PLAN_WEEKS, boardWeeks } from './care-plan-labels';

const CARD = cn(V2_CARD_SURFACE, 'rounded-card p-6 flex flex-col gap-4');
const TH = cn(V2_META, 'text-left font-medium py-2 pr-4 border-b border-border-default');
const TD = cn(V2_BODY, 'py-3 pr-4 border-b border-border-default align-top');

const TRACK_LABEL: Record<string, string> = {
  metabolic: 'Metabolic',
  function: 'Function',
  lifestyle: 'Lifestyle',
  other: 'Other',
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function targetText(g: GoalView): string {
  if (!g.target) return g.targetSet ? 'Target not readable' : 'Target not set';
  return `${g.target.comparator} ${g.target.value} ${g.target.unit}`.trim();
}

function latestText(g: GoalView): string {
  if (!g.latest) return 'No result on file';
  const v = g.latest.value ?? g.latest.valueText ?? '';
  return `${v} ${g.latest.unit} · ${formatDate(g.latest.date)}`.trim();
}

function GoalStatusChip({ status }: { status: GoalView['status'] }) {
  if (status === 'met') return <StatusChip semantic="success" tone="soft" size="xs">In target</StatusChip>;
  if (status === 'not-met') return <StatusChip semantic="warning" tone="soft" size="xs">Outside target</StatusChip>;
  if (status === 'no-data') return <StatusChip semantic="neutral" tone="soft" size="xs">No result</StatusChip>;
  return <StatusChip semantic="neutral" tone="soft" size="xs">Not judged</StatusChip>;
}

function planMeta(p: CarePlanView): string {
  const parts = [p.status === 'unknown' ? null : `Status: ${p.status}`];
  if (p.currentStep.state === 'known') parts.push(`Step: ${p.currentStep.name}`);
  if (p.period?.start) parts.push(`Plan start ${formatDate(p.period.start)}`);
  else parts.push('Plan start: not signed yet');
  if (p.period?.end) parts.push(`Ends ${formatDate(p.period.end)}`);
  return parts.filter(Boolean).join(' · ');
}

export function TrajectoryView({ plan }: { plan: CarePlanView }) {
  const withTarget = plan.goals.filter((g) => g.target);
  const outside = withTarget.filter((g) => g.status === 'not-met').length;
  return (
    <section aria-label="Trajectory" className={CARD}>
      <div className="flex flex-col gap-1">
        <h3 className={cn(V2_CARD_TITLE, 'm-0')}>Target goals · {withTarget.length}</h3>
        <p className={cn(V2_META, 'm-0')}>
          {withTarget.length === 0
            ? 'No goal on this plan has a target yet.'
            : outside === 0
              ? `${withTarget.length} measures with a target: none outside it at the latest result`
              : `${withTarget.length} measures with a target: ${outside} outside it at the latest result`}
        </p>
        <p className={cn(V2_META, 'm-0')}>{planMeta(plan)}</p>
      </div>
      {plan.goals.length === 0 ? (
        <EmptyState icon="info" title="No goals on this plan" description="The care plan carries no goals yet." />
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={TH}>Measure</th>
              <th className={TH}>Target · source</th>
              <th className={TH}>Latest value</th>
              <th className={TH}>Status</th>
            </tr>
          </thead>
          <tbody>
            {plan.goals.map((g) => (
              <tr key={g.id} data-goal-id={g.id}>
                <td className={cn(TD, V2_BODY_EMPHASIS)}>{g.title}</td>
                <td className={TD}>
                  <div>{targetText(g)}</div>
                  {g.target?.source ? <div className={V2_META}>{g.target.source}</div> : null}
                </td>
                <td className={TD}>{latestText(g)}</td>
                <td className={TD}>
                  <GoalStatusChip status={g.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** Grid placement of a plan item on the 12 weeks. Literal class names, so the styles are generated (no inline style). */
const COL_START: Record<number, string> = {
  1: 'col-start-1', 2: 'col-start-2', 3: 'col-start-3', 4: 'col-start-4', 5: 'col-start-5', 6: 'col-start-6',
  7: 'col-start-7', 8: 'col-start-8', 9: 'col-start-9', 10: 'col-start-10', 11: 'col-start-11', 12: 'col-start-12',
};
const COL_END: Record<number, string> = {
  2: 'col-end-2', 3: 'col-end-3', 4: 'col-end-4', 5: 'col-end-5', 6: 'col-end-6', 7: 'col-end-7',
  8: 'col-end-8', 9: 'col-end-9', 10: 'col-end-10', 11: 'col-end-11', 12: 'col-end-12', 13: 'col-end-13',
};

function weeksText(a: CarePlanActivityView): string {
  if (a.weekFrom > PLAN_WEEKS) return `after W${PLAN_WEEKS}`;
  const range = a.weekTo !== a.weekFrom ? `W${a.weekFrom}–W${a.weekTo}` : `W${a.weekFrom}`;
  return a.weekTo > PLAN_WEEKS ? `${range} (runs past W${PLAN_WEEKS})` : range;
}

/** One plan item. Review (accept) and Sign take a second, explicit step: an AI draft is never accepted on one click. */
function ActivityItem({
  activity: a,
  busy,
  showOwner,
  onAction,
}: {
  activity: CarePlanActivityView;
  /** Any Accept / Sign in flight: every button waits (one request at a time). */
  busy: boolean;
  /** The lane does not name one owner for all its items, so each item names its own. */
  showOwner: boolean;
  onAction?: (kind: ActivityAction) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const { from, to } = boardWeeks(a.weekFrom, a.weekTo);
  const kind: ActivityAction = a.status === 'accepted' ? 'sign' : 'accept';
  return (
    <li
      data-activity-id={a.id}
      data-status={a.status}
      className={cn(
        COL_START[from],
        COL_END[to + 1],
        'min-w-40 rounded-control border border-border-card bg-surface-card px-3 py-2 flex items-center justify-between gap-2',
      )}
    >
      <span className="flex flex-col min-w-0">
        <span className={cn(V2_BODY, 'truncate')}>{a.kind === 'retest' ? `Retest · ${a.title}` : a.title}</span>
        <span className={V2_META}>
          {ACTIVITY_STATUS_LABEL[a.status]} · {weeksText(a)}
          {showOwner && a.ownerRole ? ` · ${a.ownerRole}` : ''}
          {a.status === 'signed' && a.signedBy ? ` · ${a.signedBy}` : ''}
        </span>
      </span>
      {onAction && a.status !== 'signed' ? (
        confirming ? (
          <span className="flex items-center gap-1">
            <Button
              variant="primary"
              size="sm"
              disabled={busy}
              onClick={() => {
                setConfirming(false);
                onAction(kind);
              }}
            >
              {kind === 'sign' ? 'Confirm sign' : 'Confirm accept'}
            </Button>
            <Button variant="tertiary" size="sm" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </span>
        ) : (
          <Button variant="tertiary" size="sm" disabled={busy} onClick={() => setConfirming(true)}>
            {kind === 'sign' ? 'Sign' : 'Review'}
          </Button>
        )
      ) : null}
    </li>
  );
}

export interface PlanBoardProps {
  plan: CarePlanView;
  /** Review (accept) or Sign one item; absent = the board is read-only. */
  onAction?: (carePlanId: string, activity: CarePlanActivityView, action: ActivityAction) => void;
  /** An Accept / Sign is in flight: every Review and Sign button is disabled. */
  busy?: boolean;
  /** The last refusal, with the plan it belongs to; shown only on that plan's board. */
  actionError?: { carePlanId: string; message: string } | null;
}

export function PlanBoardView({ plan, onAction, busy = false, actionError = null }: PlanBoardProps) {
  const byTrack = new Map<string, GoalView[]>();
  for (const g of plan.goals) {
    const t = g.track && TRACK_LABEL[g.track] ? g.track : 'other';
    byTrack.set(t, [...(byTrack.get(t) ?? []), g]);
  }
  const signed = plan.goals.filter((g) => g.signoff).length;
  return (
    <div className="flex flex-col gap-4">
      <section aria-label="Care plan" className={CARD}>
        <div className="flex flex-col gap-1">
          <h3 className={cn(V2_CARD_TITLE, 'm-0')}>{plan.title}</h3>
          <p className={cn(V2_META, 'm-0')}>{planMeta(plan)}</p>
          <p className={cn(V2_META, 'm-0')}>
            {plan.goals.length === 0 ? 'No goals yet' : `${signed} of ${plan.goals.length} goals signed`}
          </p>
        </div>
        {[...byTrack.entries()].map(([track, goals]) => (
          <div key={track} className="flex flex-col gap-2" data-track={track}>
            <p className={cn(V2_META, 'm-0 font-medium')}>{TRACK_LABEL[track]}</p>
            <ul className="m-0 p-0 list-none flex flex-col gap-2">
              {goals.map((g) => (
                <li key={g.id} className="flex items-center justify-between gap-4">
                  <span className={V2_BODY}>{g.title}</span>
                  <span className={V2_META}>{g.signoff ? `Signed · ${g.signoff.signedBy}` : 'Proposed · unsigned'}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>
      <section aria-label="Lanes" className={CARD}>
        <div className="flex items-baseline justify-between gap-4">
          <h3 className={cn(V2_CARD_TITLE, 'm-0')}>{PLAN_WEEKS}-week care plan</h3>
          {plan.activities ? (
            <span className={V2_META}>
              {plan.activities.filter((a) => a.status === 'signed').length} of {plan.activities.length} signed
            </span>
          ) : null}
        </div>
        {plan.activities === undefined ? (
          <p className={cn(V2_META, 'm-0')}>This environment does not record plan items yet.</p>
        ) : null}
        <div className="flex flex-col">
          <div className="flex gap-2">
            <span className={cn(V2_META, 'w-48 shrink-0 py-2')}>Lane</span>
            <div className="flex-1 grid grid-cols-12 gap-x-2">
              {Array.from({ length: PLAN_WEEKS }, (_, i) => (
                <span key={i} className={cn(V2_META, 'py-2 text-center')}>
                  W{i + 1}
                </span>
              ))}
            </div>
          </div>
          {CARE_PLAN_LANE_IDS.map((lane) => {
            const items = (plan.activities ?? []).filter((a) => a.lane === lane);
            const roles = new Set(items.map((a) => a.ownerRole ?? ''));
            const laneOwner = items.length > 0 && roles.size === 1 ? items[0].ownerRole : null;
            return (
              <div key={lane} role="group" aria-label={CARE_PLAN_LANE_LABEL[lane]} className="flex gap-2 border-t border-border-default" data-lane={lane}>
                <div className="w-48 shrink-0 py-3 flex flex-col">
                  <span className={V2_BODY_EMPHASIS}>{CARE_PLAN_LANE_LABEL[lane]}</span>
                  {laneOwner ? <span className={V2_META}>{laneOwner}</span> : null}
                </div>
                {items.length === 0 ? (
                  <span className={cn(V2_META, 'flex-1 py-3')}>No plan items yet</span>
                ) : (
                  <ul className="flex-1 m-0 p-0 list-none grid grid-cols-12 gap-x-2 gap-y-2 py-3">
                    {items.map((a) => (
                      <ActivityItem
                        key={a.id}
                        activity={a}
                        busy={busy}
                        showOwner={!laneOwner}
                        onAction={onAction ? (kind) => onAction(plan.carePlanId, a, kind) : undefined}
                      />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
        {actionError && actionError.carePlanId === plan.carePlanId ? (
          <p role="alert" className={cn(V2_META, 'm-0')}>
            {actionError.message}
          </p>
        ) : null}
        <p className={cn(V2_META, 'm-0')}>Orders: physician only · nothing happens until signed</p>
      </section>
    </div>
  );
}

export interface CarePlanTabViewProps extends Omit<PlanBoardProps, 'plan'> {
  state: { kind: 'loading' } | { kind: 'error' } | { kind: 'empty' } | { kind: 'plans'; plans: CarePlanView[] };
}

export function CarePlanTabView({ state, ...board }: CarePlanTabViewProps) {
  const [mode, setMode] = useState<'board' | 'trajectory'>('board');
  if (state.kind === 'loading') return <p className={cn(V2_META, 'm-0 p-6')}>Reading the care plan…</p>;
  if (state.kind === 'error')
    return <EmptyState icon="info" title="The care plan could not be read" description="Try again later. Nothing on the record has changed." />;
  if (state.kind === 'empty')
    return <EmptyState icon="info" title="No care plan" description="This patient is not on a care plan." />;
  return (
    <div className="flex flex-col gap-4 p-6" data-slot="care-plan-tab">
      <Segmented
        aria-label="Care plan view"
        options={[
          { label: 'Plan board', value: 'board' },
          { label: 'Trajectory', value: 'trajectory' },
        ]}
        value={mode}
        onChange={(v: string) => setMode(v === 'trajectory' ? 'trajectory' : 'board')}
      />
      {state.plans.map((p) =>
        mode === 'board' ? <PlanBoardView key={p.carePlanId} plan={p} {...board} /> : <TrajectoryView key={p.carePlanId} plan={p} />,
      )}
    </div>
  );
}
